/**
 * The snow-sword art (id snow-sword; display name from the game data), hand-made in the sample-page style (replaces the
 * C3 generator skeleton). The art's own look, matched to its hand-made dash ss-frost (portSnow.ts, sample 'snow'): white
 * snow lit from the upper right with azure shadows and a steel ground tone; six-armed snowflakes, faceted ice shards,
 * rime crusts on the ground, soft snow clumps and quiet falling flakes. No gold, no glyph circle, no beams. Each move is
 * staged anticipation -> strike -> impact -> dissipation; body-moving moves leave split-body afterimages. Roles from the
 * data (kit audit 10-13): ss-shift is the lock (its routine data/longMoves/snowSword.ts: bind, two skating crescents,
 * snow flicked on, the load, the bough snapping up), ss-trace a plain forward dash with a wind-up. Original designs.
 */
import { add, blade, crescent, diamond, hash, isoRing, lerp, line, px, unit, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { bladeArc, C, clamp01, fade, hitStar, isoDisc, level, ringOn, since, strikeAt, tilesPx, type ArtFx } from '../families/swordKit';

// ── The art's pieces ──

/**
 * A six-armed snowflake at `c`, arm length `r`, turned by `rot`: barbs on the arms from r 4, the arms two cells thick
 * (a shadow row under) from r 7 so the pen outlines them; white tips and a white core.
 */
function flake(g: G, c: V, r: number, rot: number, lvl: number, arm = C('azure', 5)): void {
  if (lvl <= 0 || r < 1) return;
  level(g, lvl);
  const tip = C('white', 8);
  for (let k = 0; k < 6; k++) {
    const a = rot + (k * Math.PI) / 3;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    const e: V = [c[0] + ca * r, c[1] + sa * r];
    if (r >= 7) line(g, [c[0], c[1] + 1], [e[0], e[1] + 1], C('azure', 3));
    line(g, c, e, arm);
    if (r >= 4)
      for (const sg of [-1, 1]) {
        const m: V = [c[0] + ca * r * 0.55, c[1] + sa * r * 0.55];
        const b = a + sg * 0.95;
        line(g, m, [m[0] + Math.cos(b) * r * 0.32, m[1] + Math.sin(b) * r * 0.32], arm);
      }
    px(g, e[0], e[1], tip);
  }
  diamond(g, c[0], c[1], r >= 6 ? 1 : 0, arm, tip);
  g.globalAlpha = 1;
}

/**
 * A faceted ice shard from `base` along `dir`, `L` cells long, `w` cells half-wide at its widest (a third of the way
 * up): the lit side (toward the upper right) pale, the shade side deep azure, a white ridge toward the point.
 */
function shard(g: G, base: V, dir: V, L: number, w: number, lvl: number): void {
  if (lvl <= 0 || L < 2) return;
  const d = unit(dir);
  const n: V = [-d[1], d[0]];
  const lit = n[0] - n[1] >= 0 ? 1 : -1;
  const tip = add(base, d, L);
  const x0 = Math.floor(Math.min(base[0], tip[0]) - w - 1);
  const x1 = Math.ceil(Math.max(base[0], tip[0]) + w + 1);
  const y0 = Math.floor(Math.min(base[1], tip[1]) - w - 1);
  const y1 = Math.ceil(Math.max(base[1], tip[1]) + w + 1);
  level(g, lvl);
  for (let y = y0; y <= y1; y++)
    for (let x = x0; x <= x1; x++) {
      const rx = x - base[0];
      const ry = y - base[1];
      const u = (rx * d[0] + ry * d[1]) / L;
      if (u < 0 || u > 1) continue;
      const across = rx * n[0] + ry * n[1];
      const hw = w * (u < 0.3 ? 0.55 + (0.45 * u) / 0.3 : (1 - u) / 0.7) + 0.4;
      if (Math.abs(across) > hw) continue;
      const col =
        Math.abs(across) < 0.6
          ? u > 0.45
            ? C('white', 8)
            : C('azure', 6)
          : across * lit > 0
            ? C('azure', 5)
            : Math.abs(across) > hw - 1
              ? C('azure', 2)
              : C('azure', 3);
      px(g, x, y, col);
    }
  g.globalAlpha = 1;
}

/** A soft snow clump at `c` (its base row's middle), `r` cells: three round lumps, white top, azure underside. */
function clump(g: G, c: V, r: number, seed: number, lvl: number): void {
  if (lvl <= 0 || r < 1) return;
  const lumps: [number, number, number][] = [
    [-0.6 * r, 0.15 * r, r * (0.6 + 0.2 * hash(seed, 1))],
    [0, -0.25 * r, r],
    [0.6 * r, 0.1 * r, r * (0.65 + 0.2 * hash(seed, 2))],
  ];
  level(g, lvl);
  const hi = C('white', 8);
  const mid = C('white', 6);
  const lo = C('azure', 4);
  for (let y = Math.floor(c[1] - r * 1.4); y <= Math.ceil(c[1] + r * 0.9); y++)
    for (let x = Math.floor(c[0] - r * 1.4); x <= Math.ceil(c[0] + r * 1.4); x++) {
      let best = 9;
      for (const [ox, oy, rr] of lumps) {
        const dx = (x - c[0] - ox) / rr;
        const dy = (y - c[1] - oy) / rr;
        if (dx * dx + dy * dy <= 1) best = Math.min(best, dy - 0.4 * dx);
      }
      if (best > 8) continue;
      px(g, x, y, best < -0.4 ? hi : best < 0.3 ? mid : lo);
    }
  g.globalAlpha = 1;
}

/**
 * Flakes falling through a box `w` wide and `h` tall whose floor's middle is `c` (`e` frames in), drifting side to side,
 * leaning by `slant` (cells across per cell down); every third flake a small plus, the rest a white cell over azure.
 */
function snowfall(g: G, c: V, w: number, h: number, e: number, n: number, seed: number, lvl: number, slant = 0, speed = 2.2): void {
  if (lvl <= 0 || e < 0) return;
  level(g, lvl);
  for (let i = 0; i < n; i++) {
    const sp = speed * (0.7 + hash(i, seed + 1) * 0.6);
    const fall = (e * sp + hash(i, seed + 2) * h) % h;
    const y = c[1] - h + fall;
    const x = c[0] + (hash(i, seed) - 0.5) * w + slant * fall + Math.sin(e * 0.45 + i * 1.3) * 1.5;
    if (i % 3 === 0) diamond(g, x, y, 1, C('azure', 5), C('white', 8));
    else {
      px(g, x, y, C('white', 8));
      px(g, x, y + 1, C('azure', 5));
    }
  }
  g.globalAlpha = 1;
}

/** A rime crust on the ground at `c`, `rx` wide: a pale iso disc with a lit rim and small ice spikes round its edge. */
function crust(g: G, c: V, rx: number, seed: number, lvl: number): void {
  if (lvl <= 0 || rx < 2) return;
  isoDisc(g, c, rx, C('azure', 5), C('azure', 6), Math.min(lvl, 2));
  level(g, lvl);
  isoRing(g, c[0], c[1], Math.round(rx), C('white', 7));
  g.globalAlpha = 1;
  const n = Math.max(3, Math.round(rx / 3.5));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + hash(i, seed) * 0.5;
    const p: V = [c[0] + Math.cos(a) * rx * 0.85, c[1] + Math.sin(a) * rx * 0.42];
    shard(g, p, [Math.cos(a) * 0.35, -1], 3 + hash(i, seed + 1) * 4, 1.2, lvl);
  }
}

/** Dazed by frost: three small flakes orbiting over the foe's head. */
function dazed(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const head: V = [s.f[0], s.f[1] - s.chest * 1.9];
  const lv = fade(e, life - 6, life - 3);
  for (let i = 0; i < 3; i++) {
    const a = e * 0.55 + (i * Math.PI * 2) / 3;
    flake(g, [head[0] + Math.cos(a) * 10, head[1] + Math.sin(a) * 3], 3, e * 0.3, Math.sin(a) < 0 ? Math.min(lv, 2) : lv);
  }
}

// ── 1. ss-shift (the lock): the blade binds in frost, two skating crescents, snow flicked on, the load, the bough snaps ──

const SH_SKATE = [0.55, 0.85] as const;
const SH_FLICK = [1.05, 1.15, 1.25] as const;
const SH_SNAP = 1.6;
const shift: ArtFx = {
  id: 'snow-bough',
  skill: 'ss-shift',
  th: 'ss-shift',
  on: true,
  note: 'น้ำแข็งเกาะไล่ตามคมกระบี่ เส้นผลึกน้ำแข็งลากบนพื้นไปถึงเป้า (ตั้งท่าล็อก) -> ไถลฟันโค้งสองข้าง รอยไถลโค้งบนพื้น -> สะบัดก้อนหิมะลงบ่าเป้า หิมะร่วงทับ น้ำแข็งเกาะเท้า (ทรุด) -> กิ่งสนดีดคืน คมกระบี่ตวัดขึ้น หิมะระเบิดกระจายขึ้นฟ้า -> เกล็ดหิมะร่วงลงช้าๆ',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.3, t1: 0.7 },
    { m: 'wxCleave', t0: 0.7, t1: 1.0 },
    { m: 'wxQi', t0: 1.0, t1: 1.42 },
    { m: 'wxRise', t0: 1.42, t1: 1.95 },
  ],
  hits: [...SH_SKATE, ...SH_FLICK, SH_SNAP],
  big: SH_SNAP,
  ghosts: [0.5, 0.8],
  stepIn: 6,
  cast: [0.05, 0.4],
  pre: 0.4,
  hit: SH_SKATE[0],
  end: 2.6,
  afterimage: 'port',
  beats: [
    { t: 0.05, th: 'ผูกคมน้ำแข็ง' },
    { t: SH_SKATE[0], th: 'ไถลซ้าย' },
    { t: SH_SKATE[1], th: 'ไถลขวา' },
    { t: SH_FLICK[0], th: 'สะบัดหิมะ' },
    { t: 1.3, th: 'หิมะทับ' },
    { t: SH_SNAP, th: 'กิ่งดีดคืน' },
  ],
  under(g, s) {
    // The lock's tell: a line of ice crystals running out to the foe, a ring drawn on round its feet.
    if (s.t >= 0.05 && s.t < SH_SKATE[0] + 0.1) {
      const k = clamp01((s.t - 0.05) / 0.35);
      const from = add(s.a, s.d, 8);
      const to = add(s.f, s.d, -10);
      level(g, 2);
      for (let i = 0; i <= 8; i++) {
        const u = i / 8;
        if (u > k) break;
        const p = lerp(from, to, u);
        diamond(g, p[0], p[1] + (i % 2), i % 2 ? 1 : 0, C('azure', 5), C('white', 8));
      }
      level(g, 3);
      ringOn(g, s.f, 13, -Math.PI / 2, k, C('azure', 6), true);
      g.globalAlpha = 1;
    }
    // The skates: two crescent grooves round the foe's flanks, scored on over two frames, fading.
    SH_SKATE.forEach((h, j) => {
      const e = since(s.t, h - 0.05);
      if (e < 0 || e > 16) return;
      const a0 = j ? Math.PI * 1.1 : Math.PI * 0.95;
      const a1 = j ? Math.PI * 1.95 : Math.PI * 0.1;
      const fr = Math.min(1, (e + 1) / 3);
      level(g, fade(e, 8, 12));
      crescent(g, s.f[0], s.f[1], 22 + j * 4, a0, a0 + (a1 - a0) * fr, 3, C('white', 8), C('azure', 4), 0.5, e < 3);
      g.globalAlpha = 1;
    });
    // The load: rime spreading round the foe's feet as it sinks; on the snap it breaks.
    const ld = since(s.t, 1.3);
    if (ld >= 0 && s.t < SH_SNAP) crust(g, s.f, 6 + ld * 2.5, 7, 3);
    const e = since(s.t, SH_SNAP);
    if (e >= 0 && e < 10) {
      level(g, fade(e, 4, 7));
      isoRing(g, s.f[0], s.f[1], Math.round(14 + e * 4), e < 3 ? C('white', 8) : C('azure', 5), e < 3);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + 0.2;
        const r = 10 + e * 3.5;
        diamond(g, s.f[0] + Math.cos(a) * r, s.f[1] + Math.sin(a) * r * 0.5, e < 4 ? 1 : 0, C('azure', 5), C('white', 8));
      }
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    // The bind: frost creeping up the blade from the hand to the tip, flakes drawn in to the tip.
    if (s.t >= 0.05 && s.t < SH_SKATE[0]) {
      const { hand, tip } = s.limbs(s.t);
      const k = clamp01((s.t - 0.05) / 0.35);
      level(g, 3);
      const n = 8;
      for (let i = 0; i <= n * k; i++) {
        const p = lerp(hand, tip, i / n);
        px(g, p[0], p[1] - 1, i % 2 ? C('white', 8) : C('azure', 6));
      }
      diamond(g, tip[0], tip[1], k < 0.5 ? 1 : 2, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.6;
        const r = 16 * (1 - k) + 3;
        flake(g, [tip[0] + Math.cos(a) * r, tip[1] + Math.sin(a) * r], 2, s.t * 6, 3);
      }
    }
    bladeArc(g, s, 0.45, 1.0, 'azure', 3);
    bladeArc(g, s, 1.48, 1.85, 'azure', 4);
    const c = strikeAt(s);
    // Each skate: a spray of flakes off the contact.
    SH_SKATE.forEach((h, j) => {
      const e = since(s.t, h);
      if (e < 0 || e > 6) return;
      for (let i = 0; i < 5; i++) {
        const a = (j ? -1 : 1) * (0.3 + i * 0.35) - Math.PI / 2;
        flake(g, [c[0] + Math.cos(a) * (5 + e * 3), c[1] + Math.sin(a) * (4 + e * 2) + e], e < 3 ? 3 : 2, e * 0.4 + i, fade(e, 3, 5));
      }
      hitStar(g, s, c, e, 'azure', 0.7);
    });
    // The weight: snow falling on the foe, flicked clumps piling on its head and shoulders until the snap.
    const w = since(s.t, SH_FLICK[0] - 0.1);
    if (w >= 0 && s.t < SH_SNAP + 0.05) snowfall(g, [s.f[0], s.f[1] - s.chest * 1.2], 30, 34, w, 14, 9, 3, 0, 2.6);
    if (s.t < SH_SNAP) {
      const { tip } = s.limbs(s.t);
      let piled = 0;
      SH_FLICK.forEach((h, j) => {
        const e = since(s.t, h);
        if (e < 0) return;
        const dst: V = [s.f[0] + (j - 1) * 7, s.f[1] - s.chest * (j === 1 ? 1.75 : 1.45)];
        if (e < 2) {
          const p = lerp(tip, dst, (e + 1) / 2);
          clump(g, [p[0], p[1] - Math.sin(((e + 1) / 2) * Math.PI) * 6], 3, j, 3);
        } else piled++;
      });
      const ld = clamp01((s.t - 1.3) / 0.3);
      if (piled > 0) clump(g, [s.f[0], s.f[1] - s.chest * 1.75], 3 + piled + ld * 2, 11, 3);
      if (piled > 1) {
        clump(g, [s.f[0] - 7, s.f[1] - s.chest * 1.42], 3 + ld * 2, 12, 3);
        clump(g, [s.f[0] + 7, s.f[1] - s.chest * 1.42], 3 + ld * 2, 13, 3);
      }
    }
    // The snap: a bough's spring — a wide crescent cut sweeping up through the foe, the snow flung up in a fan.
    const e = since(s.t, SH_SNAP);
    if (e < 0) return;
    if (e < 8) {
      level(g, fade(e, 3, 6));
      crescent(g, s.f[0] - s.d[0] * 18, s.f[1] - s.chest * 0.45, 34, Math.PI * 0.35, -Math.PI * 0.45, e < 3 ? 5 : 2, C('white', 8), C('azure', 4), 1, e < 3);
      g.globalAlpha = 1;
    }
    if (e < 12)
      for (let i = 0; i < 9; i++) {
        const a = -Math.PI / 2 + (i / 8 - 0.5) * 2.2;
        const v = 3 + hash(i, 21) * 2.5;
        const x = c[0] + Math.cos(a) * v * e;
        const y = c[1] - s.chest * 0.6 + Math.sin(a) * v * e + 0.32 * e * e;
        if (i % 3 === 1) flake(g, [x, y], e < 5 ? 6 : 4, e * 0.5 + i, fade(e, 6, 9));
        else clump(g, [x, y], Math.max(3, 7 - Math.floor(e / 3)), i + 30, fade(e, 6, 9));
      }
    hitStar(g, s, c, e, 'azure', 1.4);
    // Settling: flakes drifting down over the scene.
    if (e >= 4 && e < 14) snowfall(g, [s.f[0], s.f[1]], 60, 60, e, 14, 33, fade(e, 9, 12), 0.15, 1.6);
  },
};

// ── 2. ss-gale: an avalanche of snow poured from above onto a chosen spot, a blizzard inside the ring ──

const G_R = Math.min(64, tilesPx(2.8));
const G_FALL = 0.45;
const G_LAND = 0.85;
const galeBank = (g: G, s: Scene, front: boolean): void => {
  const e = since(s.t, G_LAND);
  if (e < 0 || e > 14) return;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2 + 0.2;
    const sn = Math.sin(a);
    if (sn >= 0 !== front) continue;
    const r = G_R * (0.55 + 0.4 * clamp01(e / 4));
    const lift = e < 4 ? e * 2 : Math.max(0, 8 - (e - 4));
    clump(g, [s.f[0] + Math.cos(a) * r, s.f[1] + sn * r * 0.5 - lift], Math.max(3, 8 - Math.floor(e / 3)), i + 40, fade(e, 8, 11));
  }
};
const gale: ArtFx = {
  id: 'snow-avalanche',
  skill: 'ss-gale',
  th: 'ss-gale',
  on: true,
  note: 'ชูกระบี่ เกล็ดหิมะลอยขึ้นฟ้า วงฟ้าวาดบอกพื้นที่ เงาดำบนพื้นโตขึ้น -> ก้อนหิมะถล่มลงจากฟ้าเฉียงเข้าวง -> หิมะทะลักเป็นกำแพงรอบวง พื้นขาว วงฝุ่นหิมะ -> พายุหิมะเฉียงในวง ขอบวงเป็นน้ำแข็ง (ช้าลง ใช้ตัวเบาไม่ได้) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxQi', t0: 0.05, t1: 0.9 }],
  hits: [G_LAND],
  big: G_LAND,
  pre: 0.3,
  hit: G_LAND,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    if (s.t >= 0.25 && s.t < G_LAND) {
      const k = clamp01((s.t - 0.25) / 0.45);
      level(g, 3);
      ringOn(g, s.f, G_R, Math.PI, k, C('azure', 5), true);
      g.globalAlpha = 1;
      // The shadow of what is coming down, growing.
      const sh = clamp01((s.t - G_FALL) / (G_LAND - G_FALL));
      if (sh > 0) isoDisc(g, s.f, G_R * (0.3 + 0.55 * sh), C('steel', 2), null, sh < 0.5 ? 1 : 2);
    }
    const e = since(s.t, G_LAND);
    if (e < 0 || e > 22) return;
    // Snow cover over the area, an ice rim at its edge (no qinggong inside), powder rings on the landing.
    isoDisc(g, s.f, G_R - 2, C('white', 6), C('white', 7), Math.min(2, fade(e, 13, 18)));
    level(g, fade(e, 15, 19));
    isoRing(g, s.f[0], s.f[1], G_R, C('azure', 6), true);
    isoRing(g, s.f[0], s.f[1], G_R - 3, C('azure', 4));
    g.globalAlpha = 1;
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      shard(g, [s.f[0] + Math.cos(a) * G_R, s.f[1] + Math.sin(a) * G_R * 0.5], [Math.cos(a) * 0.3, -1], 5 + hash(i, 44) * 4, 1.4, fade(e, 14, 18));
    }
    if (e < 6) {
      level(g, fade(e, 2, 4));
      isoRing(g, s.f[0], s.f[1], Math.round(G_R * (0.3 + 0.75 * (e / 5))), C('white', 8), e < 3);
      g.globalAlpha = 1;
    }
    galeBank(g, s, false);
  },
  over(g, s) {
    // The call: a glint on the raised tip, flakes rising off it into the sky.
    if (s.t >= 0.1 && s.t < G_LAND) {
      const { tip } = s.limbs(s.t);
      level(g, 3);
      diamond(g, tip[0], tip[1], s.t < 0.3 ? 1 : 2, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
      for (let i = 0; i < 5; i++) {
        const y = tip[1] - ((s.t - 0.1) * 140 + i * 9) % 50;
        flake(g, [tip[0] + (i - 2) * 4 + Math.sin(s.t * 9 + i) * 2, y], i % 2 ? 2 : 3, s.t * 5 + i, 3);
      }
    }
    // The avalanche: seven clumps pouring down at a slant into the ring, powder trailing them.
    if (s.t >= G_FALL && s.t < G_LAND + 0.1)
      for (let i = 0; i < 7; i++) {
        const u = clamp01((s.t - G_FALL - (i % 4) * 0.03) / (G_LAND - G_FALL - 0.04));
        if (u <= 0 || u >= 1) continue;
        const from: V = [s.f[0] - 46 + i * 9, s.f[1] - 118 - (i % 3) * 8];
        const to: V = [s.f[0] + (hash(i, 50) - 0.5) * G_R * 1.3, s.f[1] + (hash(i, 51) - 0.5) * G_R * 0.45];
        const p = lerp(from, to, u * u);
        const q = lerp(from, to, Math.max(0, u * u - 0.18));
        level(g, 2);
        line(g, q, p, C('white', 7));
        line(g, [q[0] + 2, q[1]], [p[0] + 2, p[1]], C('azure', 5));
        g.globalAlpha = 1;
        clump(g, p, 6 + (i % 3) * 2, i + 60, 3);
      }
    galeBank(g, s, true);
    // The blizzard: slanted snow driven across the area.
    const e = since(s.t, G_LAND);
    if (e >= 2 && e < 22) {
      const lv = fade(e, 15, 19);
      level(g, lv);
      for (let i = 0; i < 14; i++) {
        const span = G_R * 0.9;
        const ph = ((e * 5 + hash(i, 70) * span * 2) % (span * 2)) - span;
        const x = s.f[0] + (hash(i, 71) - 0.5) * G_R * 1.6 + ph * 0.6;
        const y = s.f[1] - 8 - hash(i, 72) * 40 + ph * 0.45;
        line(g, [x - 7, y - 5], [x, y], C('azure', 5));
        line(g, [x - 3, y - 2], [x + 1, y + 1], C('white', 8));
      }
      g.globalAlpha = 1;
      snowfall(g, [s.f[0], s.f[1] + 4], G_R * 1.5, 50, e, 16, 80, lv, 0.6, 3);
    }
    hitStar(g, s, s.f, e, 'azure', 1.1);
  },
};

// ── 3. ss-walk: an ice sheet under the feet, skate rings, the cleanse, a hexagonal pane of ice waiting for the blow ──

const WALK_VEIL = 0.7;
const walk: ArtFx = {
  id: 'snow-skate',
  skill: 'ss-walk',
  th: 'ss-walk',
  on: true,
  note: 'แผ่นน้ำแข็งใสแผ่ใต้เท้า แสงวาบไหลผ่าน รอยไถลวนรอบตัว -> จุดหมึกดำหลุดลอยหาย (ล้างสถานะ) -> แผ่นน้ำแข็งหกเหลี่ยมตั้งรับอยู่ด้านหน้า เกล็ดหิมะกลางแผ่น เศษน้ำแข็งสามชิ้นโคจรรอบเอว (รอรับท่า) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.2 }],
  hits: [],
  pre: 0.1,
  hit: WALK_VEIL,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, 0.1);
    if (e < 0 || e > 28) return;
    const rx = Math.min(24, 5 + e * 3);
    const lv = fade(e, 21, 25);
    // The sheet: clear ice, a lit rim, a glint sliding across it, two skate rings turning round the feet.
    isoDisc(g, s.a, rx, C('azure', 4), C('azure', 6), Math.min(2, lv));
    level(g, lv);
    isoRing(g, s.a[0], s.a[1], rx, C('white', 7));
    const gx = ((e * 4) % (rx * 2 + 10)) - rx - 5;
    if (Math.abs(gx) < rx - 3) {
      line(g, [s.a[0] + gx - 3, s.a[1] + 3], [s.a[0] + gx + 3, s.a[1] - 3], C('white', 8));
      line(g, [s.a[0] + gx + 1, s.a[1] + 3], [s.a[0] + gx + 4, s.a[1]], C('white', 7));
    }
    if (e < 20)
      for (let j = 0; j < 2; j++) {
        const a0 = e * 0.35 + j * Math.PI;
        crescent(g, s.a[0], s.a[1], rx - 5 - j * 4, a0, a0 + 1.6, 2, C('white', 8), C('azure', 5), 0.5, false);
      }
    g.globalAlpha = 1;
  },
  over(g, s) {
    // Cleanse: dark motes shaken off, rising and scattering.
    const m = since(s.t, 0.3);
    if (m >= 0 && m < 10) {
      level(g, fade(m, 5, 8));
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const x = s.a[0] + Math.cos(a) * (4 + m * 2);
        const y = s.a[1] - s.chest * (0.6 + hash(i, 3) * 0.9) - m * 1.5;
        g.fillStyle = C('ink', 2);
        g.fillRect(Math.round(x), Math.round(y), m < 5 ? 2 : 1, m < 5 ? 2 : 1);
      }
      g.globalAlpha = 1;
    }
    // The waiting guard: an upright hexagonal pane of ice in front, shimmering; three shards orbiting the waist.
    const v = since(s.t, WALK_VEIL);
    if (v < 0 || v > 20) return;
    const o = add([s.a[0], s.a[1] - s.chest * 0.95], s.d, 18);
    const R6 = Math.min(26, 10 + v * 4);
    const lv = v < 15 ? (v % 5 === 4 ? 2 : 3) : v < 18 ? 2 : 1;
    level(g, Math.min(2, lv));
    for (let y = -R6; y <= R6; y++)
      for (let x = -R6 * 0.5; x <= R6 * 0.5; x++)
        if (Math.abs(y) <= R6 - Math.abs(x) * 0.6 && (x + y) % 2 === 0) px(g, o[0] + x, o[1] + y, C('azure', 5));
    level(g, lv);
    // The pane seen nearly edge-on (it faces the foe): a tall hexagon, white lit edge, azure inner edge.
    const pts: V[] = [];
    for (let k = 0; k < 6; k++) {
      const a = -Math.PI / 2 + (k * Math.PI) / 3;
      pts.push([o[0] + Math.cos(a) * R6 * 0.5, o[1] + Math.sin(a) * R6]);
    }
    for (let k = 0; k < 6; k++) {
      const p = pts[k]!;
      const q = pts[(k + 1) % 6]!;
      line(g, [p[0] + 1, p[1]], [q[0] + 1, q[1]], C('azure', 4));
      line(g, p, q, k < 3 ? C('white', 8) : C('azure', 6));
    }
    g.globalAlpha = 1;
    flake(g, o, Math.min(7, 3 + v), v * 0.25, lv);
    for (let i = 0; i < 3; i++) {
      const a = v * 0.5 + (i * Math.PI * 2) / 3;
      const sn = Math.sin(a);
      const p: V = [s.a[0] + Math.cos(a) * 15, s.a[1] - s.chest * 0.55 + sn * 5];
      shard(g, p, [Math.cos(a + 1.6) * 0.4, -1], 6, 1.5, sn < 0 ? Math.min(2, lv) : lv);
    }
  },
};

// ── 4. ss-ice (break): frost cracks run to the foe, a heavy cut, ice spikes burst up round its feet, the guard shatters ──

const ICE_HIT = 0.72;
const iceBreak: ArtFx = {
  id: 'snow-icebreak',
  skill: 'ss-ice',
  th: 'ss-ice',
  on: true,
  note: 'วงเหล็กเทาวาดรอบเท้า รอยร้าวน้ำแข็งวิ่งบนพื้นไปหาเป้า ผลึกน้ำแข็งเกาะคมกระบี่ (ตั้งท่าทำลาย) -> ก้าวฟันหนักลง เงาร่างแยก -> หนามน้ำแข็งผุดรอบเท้าเป้า รอยร้าวดาวบนพื้น เศษการ์ดแตก -> ผนึกน้ำแข็งค้างที่เท้า เกล็ดหิมะวนเหนือหัว (มึน) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBreak', t0: 0.1, t1: 1.2 }],
  hits: [ICE_HIT],
  big: ICE_HIT,
  ghosts: [0.56, 0.62, 0.68],
  stepIn: 10,
  cast: [0.05, 0.45],
  pre: 0.45,
  hit: ICE_HIT,
  end: 2.4,
  afterimage: 'port',
  under(g, s) {
    // The break's tell: a steel ring round the feet, a jagged crack running out to the foe.
    if (s.t >= 0.05 && s.t < ICE_HIT) {
      const k = clamp01((s.t - 0.05) / 0.45);
      level(g, s.t < 0.55 ? 3 : 2);
      ringOn(g, s.a, 13, -Math.PI / 2, k, C('steel', 5), true);
      const from = add(s.a, s.d, 6);
      const to = s.f;
      let prev = from;
      for (let i = 1; i <= 10; i++) {
        const u = i / 10;
        if (u > k) break;
        const base = lerp(from, to, u);
        const p: V = [base[0], base[1] + (i % 2 ? 2 : -2) * (i < 10 ? 1 : 0)];
        line(g, prev, p, C('azure', 6));
        line(g, [prev[0], prev[1] + 1], [p[0], p[1] + 1], C('azure', 3));
        prev = p;
      }
      g.globalAlpha = 1;
    }
    const e = since(s.t, ICE_HIT);
    if (e < 0 || e > 22) return;
    // The ground cracking in a star round the foe; a frost trap left at its feet.
    level(g, fade(e, 10, 16));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.3;
      const L = Math.min(30, 10 + e * 6) * (0.7 + hash(i, 90) * 0.5);
      const mid: V = [s.f[0] + Math.cos(a + 0.2) * L * 0.5, s.f[1] + Math.sin(a + 0.2) * L * 0.25];
      const end: V = [s.f[0] + Math.cos(a) * L, s.f[1] + Math.sin(a) * L * 0.5];
      line(g, s.f, mid, C('white', 8));
      line(g, mid, end, C('azure', 5));
    }
    g.globalAlpha = 1;
    crust(g, s.f, 13, 91, fade(e, 14, 19));
  },
  over(g, s) {
    // Wind-up: ice crystals forming along the blade, flakes drawn in.
    if (s.t >= 0.1 && s.t < ICE_HIT) {
      const { hand, tip } = s.limbs(s.t);
      const k = clamp01((s.t - 0.1) / 0.4);
      for (let i = 1; i <= 3; i++) {
        const p = lerp(hand, tip, 0.35 + i * 0.2);
        if (i / 3 > k + 0.2) continue;
        shard(g, p, [0.3 * (i - 2), -1], 3 + i, 1, 3);
      }
      level(g, 3);
      diamond(g, tip[0], tip[1], k < 0.5 ? 1 : 2, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    bladeArc(g, s, 0.6, 1.0, 'azure', 4);
    const e = since(s.t, ICE_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    // Ice spikes bursting up round the foe's feet, growing over two frames, then the front ones staying as the trap.
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + 0.25;
      const sn = Math.sin(a);
      const stays = sn > 0.2;
      if (e > (stays ? 18 : 9)) continue;
      const base: V = [s.f[0] + Math.cos(a) * 15, s.f[1] + sn * 7.5];
      const L = (e < 2 ? (e + 1) * 9 : 27) * (0.7 + hash(i, 95) * 0.5) * (stays && e > 8 ? 0.6 : 1);
      shard(g, base, [Math.cos(a) * 0.5, -1], L, 3.4, stays ? fade(e, 13, 16) : fade(e, 5, 8));
    }
    // The guard shattering: steel and ice shards flung out and falling.
    if (e >= 1 && e < 9) {
      level(g, fade(e, 5, 7));
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + hash(i, 96) * 0.5;
        const vv = 2.4 + hash(i, 97) * 1.6;
        const x = c[0] + Math.cos(a) * vv * e;
        const y = c[1] + Math.sin(a) * vv * e * 0.7 + 0.3 * e * e;
        if (i % 2) diamond(g, x, y, 1, C('steel', 5), C('white', 8));
        else line(g, [x, y], [x + Math.cos(a) * 3, y + Math.sin(a) * 3], C('azure', 6));
      }
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, e, 'azure', 1.3);
    dazed(g, s, e - 4, 16);
  },
};

// ── 5. ss-pride: standing still in the cold wind, a great snowflake drawn arm by arm overhead, stacks as small flakes ──

const PRIDE_SET = 0.75;
const pride: ArtFx = {
  id: 'snow-pride',
  skill: 'ss-pride',
  th: 'ss-pride',
  on: true,
  note: 'ยืนนิ่ง ลมหนาวพาเกล็ดหิมะพัดผ่านตัว วงน้ำแข็งที่เท้า -> เกล็ดหิมะยักษ์หกแฉกวาดทีละแฉกเหนือหัว -> สว่างวาบ หดเป็นเกล็ดเล็กสามดวงโคจรรอบหัว (ใจเหมันต์ 3 ชั้น) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 1.7 }],
  hits: [],
  pre: 0.1,
  hit: PRIDE_SET,
  end: 2.3,
  afterimage: null,
  under(g, s) {
    const e = since(s.t, 0.15);
    if (e < 0 || e > 24) return;
    const lv = fade(e, 17, 21);
    level(g, lv);
    isoRing(g, s.a[0], s.a[1], 15, C('azure', 6), true);
    isoRing(g, s.a[0], s.a[1], 11, C('white', 7));
    g.globalAlpha = 1;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.4;
      if (i / 8 > (e + 1) / 6) continue;
      shard(g, [s.a[0] + Math.cos(a) * 15, s.a[1] + Math.sin(a) * 7.5], [Math.cos(a) * 0.25, -1], 4 + (i % 2) * 2, 1.2, lv);
    }
  },
  over(g, s) {
    const e = since(s.t, 0.1);
    if (e < 0 || e > 26) return;
    // The cold wind: snow-laden streaks blowing past the still body from the front to the back.
    if (e < 20) {
      level(g, fade(e, 14, 18));
      for (let i = 0; i < 7; i++) {
        const span = 90;
        const x = s.a[0] + s.d[0] * (45 - ((e * 9 + hash(i, 100) * span) % span));
        const y = s.a[1] - s.chest * (0.3 + hash(i, 101) * 1.6);
        line(g, [x, y], [x + 8, y + 1], C('azure', 5));
        px(g, x - 1, y, C('white', 8));
        if (i % 2) px(g, x - 2, y + 1, C('white', 8));
      }
      g.globalAlpha = 1;
    }
    // The great flake over the head, drawn arm by arm, flashing when whole, then gathered into three small flakes.
    const top: V = [s.a[0], s.a[1] - s.chest * 2.0];
    const k = clamp01((s.t - 0.2) / (PRIDE_SET - 0.2));
    if (s.t < PRIDE_SET) {
      const arms = Math.ceil(k * 6);
      level(g, 3);
      for (let i = 0; i < arms; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 3;
        const p: V = [top[0] + Math.cos(a) * 12, top[1] + Math.sin(a) * 12];
        line(g, [top[0], top[1] + 1], [p[0], p[1] + 1], C('azure', 3));
        line(g, top, p, C('azure', 6));
        px(g, p[0], p[1], C('white', 8));
      }
      g.globalAlpha = 1;
    }
    const f = since(s.t, PRIDE_SET);
    if (f >= 0 && f < 6) flake(g, top, 12 - f, -Math.PI / 2 + f * 0.12, f < 2 ? 3 : 2, f < 2 ? C('white', 8) : C('azure', 6));
    if (f >= 0 && f < 2) {
      level(g, 2);
      isoRing(g, top[0], top[1], 16 + f * 4, C('white', 8));
      g.globalAlpha = 1;
    }
    if (f >= 3 && f < 18)
      for (let i = 0; i < 3; i++) {
        const a = f * 0.4 + (i * Math.PI * 2) / 3;
        const r = Math.min(14, 3 + (f - 3) * 2);
        flake(g, [top[0] + Math.cos(a) * r, top[1] + 6 + Math.sin(a) * r * 0.35], 4, f * 0.3, Math.sin(a) < 0 ? 2 : fade(f, 13, 16));
      }
  },
};

// ── 6. ss-trace: the hush of the wind-up, a silent dash, snow fluff left hanging on the path, the delayed cut ──

const TRACE = 100;
const TR_GO = 0.55;
const TR_HIT = 0.72;
const trace: ArtFx = {
  id: 'snow-fluff',
  skill: 'ss-trace',
  th: 'ss-trace',
  on: true,
  note: 'นิ่งเงียบ วงขาวหดเข้าหาเท้า ปุยหิมะลอยเข้าคมกระบี่ (ตั้งท่า) -> พุ่งไปข้างหน้าเงียบๆ เงาร่างแยก ปุยหิมะค้างกลางอากาศตามทาง -> รอยฟันเส้นบางปรากฏตามหลังทั้งแนว แตกเป็นเกล็ดหิมะ -> ปุยหิมะค่อยๆ ร่วงลงพื้น',
  weapon: 'sword',
  acts: [{ m: 'wxThrust', t0: 0.3, t1: 1.15 }],
  hits: [TR_HIT],
  big: TR_HIT,
  ghosts: [0.6, 0.64, 0.68],
  cast: [0.05, 0.5],
  pre: 0.5,
  hit: TR_HIT,
  end: 2.4,
  dash: TRACE,
  afterimage: 'dash',
  move(s, t) {
    const k0 = clamp01((t - TR_GO) / 0.2);
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 2.1) / 0.3);
    return [s.d[0] * TRACE * k * back, s.d[1] * TRACE * k * back];
  },
  under(g, s) {
    // The hush: a white ring closing in on the feet.
    if (s.t >= 0.05 && s.t < TR_GO) {
      const k = clamp01((s.t - 0.05) / 0.45);
      level(g, k < 0.6 ? 2 : 3);
      isoRing(g, s.a[0], s.a[1], Math.round(26 - k * 16), C('white', 8), k > 0.6);
      g.globalAlpha = 1;
    }
    // Fluff come down on the path, lying as small white flecks, dithered away.
    const e = since(s.t, 1.25);
    if (e >= 0 && e < 14) {
      level(g, fade(e, 6, 10));
      for (let i = 0; i < 10; i++) {
        const p = add(s.a, s.d, 10 + i * 9 + hash(i, 120) * 4);
        g.fillStyle = C('white', 7);
        g.fillRect(Math.round(p[0]), Math.round(p[1] + (hash(i, 121) - 0.5) * 6), 2, 1);
      }
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    // Wind-up: fluff drifting in to the blade, a soft glint at the tip.
    if (s.t >= 0.1 && s.t < TR_GO) {
      const { tip } = s.limbs(s.t);
      const k = clamp01((s.t - 0.1) / 0.4);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 + 0.3;
        const r = 20 * (1 - k) + 3;
        clump(g, [tip[0] + Math.cos(a) * r, tip[1] + Math.sin(a) * r * 0.6], 2, i + 130, k < 0.3 ? 2 : 3);
      }
      level(g, 3);
      diamond(g, tip[0], tip[1], k < 0.5 ? 1 : 2, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    // Fluff left hanging where the body passed, sinking slowly.
    const pass = since(s.t, TR_GO);
    if (pass >= 0 && pass < 22) {
      const b = s.body(s.t);
      const done = Math.hypot(b[0] - s.a[0], b[1] - s.a[1]);
      for (let i = 0; i < 9; i++) {
        const x = 12 + i * 10;
        if (x > done - 6) continue;
        const born = Math.max(0, pass - Math.floor((x / TRACE) * 3));
        const p = add(s.a, s.d, x);
        const h = s.chest * (i % 3 === 0 ? 1.1 : i % 3 === 1 ? 0.55 : 1.5);
        const y = p[1] - h + Math.min(h - 2, born * born * 0.12);
        const lv = fade(pass, 13, 18);
        clump(g, [p[0] + Math.sin(born * 0.4 + i) * 1.5, y], i % 2 ? 3 : 4, i + 140, lv);
      }
    }
    // The delayed cut: a thin white line along the whole path, then breaking up into flakes.
    const e = since(s.t, TR_HIT);
    if (e >= 0 && e < 12) {
      const a = add([s.a[0], s.a[1] - s.chest], s.d, -4);
      const b = add([s.a[0], s.a[1] - s.chest], s.d, TRACE + 14);
      if (e < 3) {
        level(g, e < 2 ? 3 : 2);
        blade(g, a, b, 3, C('white', 8), C('azure', 4), 0.7);
        g.globalAlpha = 1;
      } else
        for (let i = 0; i < 12; i++) {
          const p = lerp(a, b, (i + 0.5) / 12);
          flake(g, [p[0], p[1] + (e - 3) * 1.5 + (i % 2) * 2], i % 3 ? 2 : 3, e * 0.4 + i, fade(e, 8, 10));
        }
    }
    hitStar(g, s, strikeAt(s), e, 'azure', 1.0);
  },
};

// ── 7. ss-hatred (ท่าใหญ่): gathered frost, the vanish, five warp cuts round the foe sealing it in ice, the shatter ──

const H_R = Math.min(78, tilesPx(3));
const H_VANISH = 0.85;
const H_CUTS = [1.0, 1.3, 1.6, 1.9, 2.2] as const;
const H_FIN = 2.6;
/** Where each warp puts the body round the foe (angle round it, the iso ground squashing y). */
const H_ANG = [Math.PI * 0.95, 0.15, Math.PI * 1.25, Math.PI * 0.6, -0.5] as const;
const warpAt = (s: Scene, k: number): V => [s.f[0] + Math.cos(H_ANG[k]!) * 34, s.f[1] + Math.sin(H_ANG[k]!) * 15];
function hatredMove(s: Scene, t: number): V {
  if (t < H_CUTS[0] - 0.08 || t >= H_FIN + 0.35) return [0, 0];
  if (t >= H_FIN - 0.1) {
    const p = add(s.f, s.d, -20);
    return [p[0] - s.a[0], p[1] - s.a[1]];
  }
  let k = 0;
  for (let i = 0; i < H_CUTS.length; i++) if (t >= H_CUTS[i]! - 0.08) k = i;
  const p = warpAt(s, k);
  return [p[0] - s.a[0], p[1] - s.a[1]];
}
/** The ice seal growing round the foe: two shards per cut landed, standing round its feet and leaning in. */
function seal(g: G, s: Scene, front: boolean): void {
  if (s.t < H_CUTS[0] || s.t >= H_FIN + 0.05) return;
  let n = 0;
  for (const h of H_CUTS) if (s.t >= h) n++;
  for (let i = 0; i < n * 2; i++) {
    const a = (i / 10) * Math.PI * 2 + 0.3;
    const sn = Math.sin(a);
    if (sn >= 0 !== front) continue;
    const base: V = [s.f[0] + Math.cos(a) * 11, s.f[1] + sn * 5.5];
    shard(g, base, [-Math.cos(a) * 0.35, -1], s.chest * (0.9 + hash(i, 160) * 0.6), 3, 3);
  }
}
const hatred: ArtFx = {
  id: 'snow-hatred',
  skill: 'ss-hatred',
  th: 'ss-hatred',
  on: true,
  note: 'เกล็ดหิมะหมุนวนเข้าหาตัว วงฟ้าวาดบอกรัศมี -> ระเบิดหิมะ ร่างหายวับ -> วาปโผล่ข้างเป้าห้าทิศ ทุกจุดมีหิมะฟุ้ง ฟันโค้งคนละแนว เงาร่างแยก แต่ละฟันเพิ่มแท่งน้ำแข็งล้อมเป้า -> ฟันปิดท้าย ผนึกน้ำแข็งแตกกระจาย วงหิมะกวาดทั้งวง -> หิมะโปรยลง ศัตรูมึน -> จาง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 0.9 },
    { m: 'snowRise', t0: 0.92, t1: 1.22 },
    { m: 'snowSweep', t0: 1.22, t1: 1.52 },
    { m: 'snowCleave', t0: 1.52, t1: 1.82 },
    { m: 'snowThrust', t0: 1.82, t1: 2.12 },
    { m: 'snowRise', t0: 2.12, t1: 2.45 },
    { m: 'snowFinale', t0: 2.45, t1: 3.1 },
  ],
  hits: [...H_CUTS, H_FIN],
  big: H_FIN,
  ghosts: [0.96, 1.26, 1.56, 1.86, 2.16, 2.52],
  stepIn: 8,
  cast: [0.1, 0.8],
  pre: 0.8,
  hit: H_CUTS[0],
  end: 3.6,
  afterimage: 'port',
  move: hatredMove,
  beats: [
    { t: 0.05, th: 'รวมเหมันต์' },
    { t: H_VANISH, th: 'หายวับ' },
    { t: H_CUTS[0], th: 'วาปฟันห้าทิศ' },
    { t: H_FIN, th: 'ผนึกแตก' },
    { t: 3.0, th: 'หิมะโปรย' },
  ],
  under(g, s) {
    if (s.t >= 0.15 && s.t < H_CUTS[0]) {
      level(g, 3);
      ringOn(g, s.a, H_R, -Math.PI / 2, (s.t - 0.15) / 0.6, C('azure', 5), true);
      g.globalAlpha = 1;
    }
    // Frost under the foe thickening with each cut.
    if (s.t >= H_CUTS[0] && s.t < H_FIN) {
      let n = 0;
      for (const h of H_CUTS) if (s.t >= h) n++;
      isoDisc(g, s.f, 8 + n * 3, C('azure', 4), C('azure', 6), 2);
    }
    seal(g, s, false);
    const e = since(s.t, H_FIN);
    if (e < 0 || e > 22) return;
    // The shatter: a snow sweep across the whole area, then a frost floor fading.
    for (let k = 0; k < 2; k++) {
      const ee = e - k * 2;
      if (ee < 0 || ee > 8) continue;
      level(g, fade(ee, 3, 6));
      isoRing(g, s.f[0], s.f[1], Math.round(H_R * (0.3 + 0.7 * clamp01(ee / 5))), k ? C('azure', 5) : C('white', 8), k === 0);
    }
    g.globalAlpha = 1;
    crust(g, s.f, 20, 170, fade(e, 12, 18));
  },
  over(g, s) {
    // The gather: flakes spiralling in from past the area's edge, rising round the body.
    if (s.t >= 0.05 && s.t < H_VANISH + 0.05) {
      for (let i = 0; i < 14; i++) {
        const k = clamp01((s.t - 0.05 - (i % 5) * 0.08) / 0.5);
        if (k <= 0 || k >= 1) continue;
        const a = (i / 14) * Math.PI * 2 + k * 3.4;
        const r = (H_R + 10) * (1 - k) + 6;
        const p: V = [s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5 - k * s.chest * 1.4];
        flake(g, p, i % 3 ? 2 : 4, a, k < 0.15 ? 2 : 3);
      }
      const { tip } = s.limbs(s.t);
      level(g, 3);
      diamond(g, tip[0], tip[1], s.t < 0.5 ? 1 : 2, C('azure', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    // The vanish: a burst of snow where the body stood.
    const v = since(s.t, H_VANISH);
    if (v >= 0 && v < 8)
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const p: V = [s.a[0] + Math.cos(a) * (4 + v * 3), s.a[1] - s.chest * 0.8 + Math.sin(a) * (3 + v * 2)];
        clump(g, p, Math.max(2, 6 - Math.floor(v / 2)), i + 180, fade(v, 4, 6));
      }
    seal(g, s, true);
    const c = strikeAt(s);
    // Five warp cuts: a puff where the body appears, a crescent through the foe at its own angle, a shard flung off.
    H_CUTS.forEach((h, k) => {
      const e = since(s.t, h - 0.08);
      if (e < 0 || e > 9) return;
      const at = warpAt(s, k);
      if (e < 5) clump(g, [at[0], at[1] - 2], Math.max(2, 6 - e), k + 190, fade(e, 2, 4));
      const ce = e - 1;
      if (ce >= 0 && ce < 7) {
        const a0 = H_ANG[k]! + Math.PI * 0.5;
        level(g, fade(ce, 3, 5));
        crescent(g, c[0], c[1], 24, a0, a0 + Math.PI * 0.9, ce < 3 ? 4 : 1, C('white', 8), C('azure', 4), 0.75, ce < 3);
        g.globalAlpha = 1;
        if (ce < 6) shard(g, [c[0] + Math.cos(a0 + 2.4) * (6 + ce * 4), c[1] + Math.sin(a0 + 2.4) * (4 + ce * 3)], [Math.cos(a0 + 2.4), Math.sin(a0 + 2.4)], 6, 1.4, fade(ce, 3, 5));
      }
      hitStar(g, s, c, since(s.t, h), 'azure', 0.8);
    });
    // The finale: a tall falling cut through the seal, the ice bursting out on every side.
    const e = since(s.t, H_FIN);
    if (e < 0) return;
    if (e < 7) {
      level(g, fade(e, 3, 5));
      crescent(g, s.f[0] - s.d[0] * 18, s.f[1] - s.chest * 1.3, 30, -Math.PI * 0.4, Math.PI * 0.45, e < 3 ? 5 : 2, C('white', 8), C('azure', 4), 1, e < 3);
      g.globalAlpha = 1;
    }
    if (e < 12)
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + 0.3;
        const vv = 3 + hash(i, 200) * 2;
        const p: V = [s.f[0] + Math.cos(a) * vv * (e + 2), s.f[1] - s.chest + Math.sin(a) * vv * (e + 2) * 0.6 + 0.3 * e * e];
        shard(g, p, [Math.cos(a), Math.sin(a)], Math.max(3, 9 - e * 0.6), 2, fade(e, 6, 9));
      }
    if (e >= 3 && e < 14) flake(g, [s.f[0], s.f[1] - s.chest * 1.2], Math.min(16, 6 + e * 2), e * 0.08, fade(e, 8, 11));
    hitStar(g, s, c, e, 'azure', 1.7);
    if (e >= 5 && e < 20) snowfall(g, s.f, H_R * 1.6, 80, e, 22, 210, fade(e, 14, 17), 0.1, 2.4);
    dazed(g, s, e - 3, 14);
  },
};

/**
 * The snow-sword moves, hand-made (each its own shape, motion and timing; one palette and motif family). ss-frost is the
 * sample port in portSnow.ts (registered there, not here).
 */
export const SNOW_FX: readonly ArtFx[] = [shift, gale, walk, iceBreak, pride, trace, hatred];
